/**
 * `roster/ReportSickDialog.tsx` — the lead's ask-once confirm behind
 * "Report off sick" on a roster day (KKH-REQ-05, hardening §9.3).
 *
 * Deliberately tiny. The lead is on the phone to the person who is not
 * coming in; the dialog exists to name who and which day out loud before
 * a write that takes effect immediately, and to say what the write does
 * to the roster ("frees their seat for cover") so nobody is surprised by
 * the −N that appears behind it.
 *
 * Failure is inline and announced (`role="alert"`), in the server's own
 * sentence — a refused MC (the team has sick handling off, the member
 * left the team) must read as a reason, not as a status code.
 */
import { useState } from "react";

import { ApiError } from "../../api/client";
import { useReportOffSick } from "../../api/useReportOffSick";
import { formatShortDayLabel } from "../../lib/publishedDays";
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui";

/** Who, and which day. Null closes the dialog. */
export interface ReportSickTarget {
  membershipId: string;
  memberName: string;
  date: string;
}

export interface ReportSickDialogProps {
  target: ReportSickTarget | null;
  onClose: () => void;
  teamId: string | undefined;
  cycleId: string;
  /** Fired after the write lands, so the page can refetch its panels. */
  onReported?: (() => void) | undefined;
}

/** The server's own sentence, never `"API 422"`. Details carry a machine
 *  code in front (`component_disabled:mc_recover`); a code with no
 *  sentence behind it gets a plain one a lead can act on. Shared with the
 *  toolbar's *Record absence* (#2029), which writes the same row. */
export function reportErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    const detail = err.detail;
    if (detail.startsWith("component_disabled:")) {
      return "This team does not have sick reporting switched on. Turn on sick cover in Setup, or record the day as leave.";
    }
    const match = /^([a-z][a-z0-9_]*):\s*(.+)$/i.exec(detail);
    const rest = match?.[2]?.trim();
    if (rest && /\s/.test(rest)) return rest;
    return detail;
  }
  if (err instanceof Error) return err.message;
  return "That could not be saved. Please try again.";
}

/**
 * Closed is UNMOUNTED, and each target is its own mount (`key`): a fresh
 * ask starts on a blank sheet, never inheriting the last one's failure
 * message or pending state, without an effect to reset it by hand.
 */
export function ReportSickDialog({ target, ...rest }: ReportSickDialogProps) {
  if (!target) return null;
  return <ReportSickBody key={`${target.membershipId}|${target.date}`} target={target} {...rest} />;
}

function ReportSickBody({
  target,
  onClose,
  teamId,
  cycleId,
  onReported,
}: Omit<ReportSickDialogProps, "target"> & { target: ReportSickTarget }) {
  const report = useReportOffSick(teamId, cycleId);
  const [error, setError] = useState<string | null>(null);

  const confirm = () => {
    setError(null);
    report.mutate(
      { membershipId: target.membershipId, date: target.date },
      {
        onSuccess: () => {
          onReported?.();
          onClose();
        },
        onError: (err) => setError(reportErrorText(err)),
      },
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-w-md" data-testid="report-sick-dialog">
        <DialogHeader>
          <DialogTitle>Report off sick</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-fg-subtle" data-testid="report-sick-question">
          Report {target.memberName} off sick on {formatShortDayLabel(target.date)}? This takes
          effect immediately and frees their seat for cover.
        </p>
        {error ? (
          <p className="mt-3 text-xs text-danger" role="alert" data-testid="report-sick-error">
            {error}
          </p>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={report.isPending}>
            Not now
          </Button>
          <Button
            type="button"
            onClick={confirm}
            disabled={report.isPending}
            data-testid="report-sick-confirm"
          >
            {report.isPending ? "Reporting…" : "Yes, report off sick"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ReportSickDialog;
